import { useMemo } from 'react';

const getFormattedMarkValue = (rawValue: number): number => parseFloat(rawValue.toFixed(1));

/**
 * Generates a label for a numeric slider mark based on its value.
 *
 * @param markValue - The value of the current mark.
 * @param minValue - The minimum value of the slider.
 * @param maxValue - The maximum value of the slider.
 * @param minLabel - Optional custom label for the minimum value mark.
 * @param maxLabel - Optional custom label for the maximum value mark.
 * @returns A string representing the label for the mark. If the mark value
 *          matches the minimum value, it returns `minLabel` if defined or the mark value
 *          as a string. If the mark value matches the maximum value, it returns
 *          `maxLabel` if defined or the mark value as a string. Otherwise, it returns an
 *          empty string.
 */
const getMarkLabel = (
  markValue: number,
  minValue: number,
  maxValue: number,
  minLabel?: string,
  maxLabel?: string,
): string => {
  if (markValue === minValue) return minLabel || `${markValue}`;
  if (markValue === maxValue) return maxLabel || `${markValue}`;
  return '';
};

export const useNumericSlider = (
  min: number,
  max: number,
  step: number,
  minLabel?: string,
  maxLabel?: string,
) => {
  const marks = useMemo(() => {
    if (step === 0 || step < 0 || min > max) {
      return [];
    }

    const markArray = [];
    for (let markValue = min; markValue <= max; markValue += step) {
      const formattedMarkValue = getFormattedMarkValue(markValue);

      markArray.push({
        value: formattedMarkValue,
        label: getMarkLabel(formattedMarkValue, min, max, minLabel, maxLabel),
      });
    }

    // If the step does not produce a mark at the provided max value, explicitly add it
    if (markArray?.[markArray.length - 1].value !== max) {
      const formattedMarkValue = getFormattedMarkValue(max);

      markArray.push({
        value: formattedMarkValue,
        label: getMarkLabel(formattedMarkValue, min, max, minLabel, maxLabel),
      });
    }

    return markArray;
  }, [min, max, step]);

  return { marks };
};
